import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';


const USER_TYPE_OPTIONS = [
  { id: 'employees', label: 'Employees (Drivers & Technicians)', icon: '👷' },
  { id: 'partners', label: 'Partners', icon: '🤝' },
  { id: 'managers', label: 'Managers', icon: '📋' },
  { id: 'admins', label: 'Admins', icon: '👤' },
];

function TestNotifications() {
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedTypes, setSelectedTypes] = useState(['employees', 'partners', 'managers', 'admins']);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleTypeToggle = (typeId) => {
    setSelectedTypes((prev) =>
      prev.includes(typeId) ? prev.filter((t) => t !== typeId) : [...prev, typeId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTypes(USER_TYPE_OPTIONS.map((o) => o.id));
  };

  const handleDeselectAll = () => {
    setSelectedTypes([]);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Please enter both title and body.');
      return;
    }
    if (selectedTypes.length === 0) {
      setError('Please select at least one user type.');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const sendNotification = httpsCallable(functions, 'sendTestNotificationToAll');
      const response = await sendNotification({
        title: title.trim(),
        body: body.trim(),
        userTypes: selectedTypes,
      });
      setResults(response.data);
    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message || 'Failed to send notifications. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setBody('');
    setSelectedTypes(['employees', 'partners', 'managers', 'admins']);
    setResults(null);
    setError('');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : isOpen ? '16rem' : '5rem' }}
      >
        <div className="p-8 max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center mb-8">
            {isMobile && (
              <button
                onClick={toggleMobileSidebar}
                className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Test Notifications</h1>
              <p className="text-gray-500 mt-1">Send test push notifications to all users with FCM tokens</p>
            </div>
          </div>

          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="text-amber-800 font-medium text-sm">Testing Only</p>
              <p className="text-amber-700 text-sm mt-0.5">This page sends real push notifications to all selected users. Use for testing purposes only.</p>
            </div>
          </div>

          {/* Compose Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Compose Notification</h2>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. System Maintenance Notice"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-brand-golden outline-none transition-all text-gray-800"
                disabled={loading}
              />
            </div>

            {/* Body */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message Body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="e.g. The system will be under maintenance from 2 AM to 4 AM."
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-brand-golden outline-none transition-all text-gray-800 resize-none"
                disabled={loading}
              />
            </div>

            {/* User Type Selection */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Send To</label>
                <div className="flex gap-2">
                  <button
                    onClick={handleSelectAll}
                    className="text-xs text-brand-golden hover:text-yellow-600 font-medium"
                    disabled={loading}
                  >
                    Select All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={handleDeselectAll}
                    className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                    disabled={loading}
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {USER_TYPE_OPTIONS.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTypes.includes(option.id)
                        ? 'border-brand-golden bg-brand-golden/5'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    } ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.includes(option.id)}
                      onChange={() => handleTypeToggle(option.id)}
                      className="w-4 h-4 text-brand-golden rounded focus:ring-brand-golden"
                      disabled={loading}
                    />
                    <span className="text-lg">{option.icon}</span>
                    <span className="text-sm font-medium text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            {/* Send Button */}
            <div className="flex gap-3">
              <button
                onClick={handleSend}
                disabled={loading || !title.trim() || !body.trim() || selectedTypes.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-golden text-white font-medium rounded-lg hover:bg-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send Notification
                  </>
                )}
              </button>
              {results && (
                <button
                  onClick={resetForm}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-all"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Results</h2>

              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{results.total}</p>
                  <p className="text-sm text-blue-700 mt-1">Total Sent</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{results.success}</p>
                  <p className="text-sm text-green-700 mt-1">Delivered</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">{results.failed}</p>
                  <p className="text-sm text-red-700 mt-1">Failed</p>
                </div>
              </div>

              {/* Details Table */}
              {results.details && results.details.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">User ID</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Collection</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Status</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.details.map((detail, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-xs text-gray-600">
                            {detail.userId.substring(0, 12)}...
                          </td>
                          <td className="py-2 px-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {detail.collection}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                detail.status === 'ok'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {detail.status === 'ok' ? 'Delivered' : 'Failed'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-xs text-gray-500">
                            {detail.messageId || detail.error || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TestNotifications;
